import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal, Spin, Steps, Table } from 'antd';
import useFetchEntities from 'hooks/useFetchEntities';
import useIsMobile from 'hooks/useIsMobile';
import { useCallback, useMemo, useState } from 'react';
import { PlayerInviteModalBody, PlayerInviteModalButtons } from './PlayerInviteModalBody';
import PlayerSelectionTable from './PlayerSelectionTable';
import SeasonSelectionTable from './SeasonSelectionTable';

const STEPS = {
  SELECT_SEASON: 0,
  SELECT_PLAYERS: 1,
  REVIEW_PLAYERS: 2,
  RESULTS: 3,
};

const BulkInvitePlayerToSeasonDialog = ({
  open,
  onClose,
  loading: externalLoading,
  onSendInvites,
  inviteResults,
}) => {

  const [step, setStep] = useState(STEPS.SELECT_SEASON);
  const [selectedSeasonIds, setSelectedSeasonIds] = useState([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const isMobile = useIsMobile();

  // Hráče a registrace načítáme až při step 1 a vybraných sezónách
  const shouldLoadPlayers = open && (step === STEPS.SELECT_PLAYERS || step === STEPS.REVIEW_PLAYERS) && selectedSeasonIds.length > 0;
  const { data: playersRaw, isLoading: playersLoading } = useFetchEntities(
    shouldLoadPlayers ? 'Player' : null,
    { relations: ['User', 'Teams(name,logoUrl)', 'ListPlayerLevel(name,code)', 'ListPositions(name)', 'SeasonPlayerRegistrations'] } // >Season(name,status)'] }
  );

  // Show all players with valid email or user account
  const players = useMemo(() => {
    if (!shouldLoadPlayers || !playersRaw) return [];
    return playersRaw.filter(p => {
      const hasUserOrEmail = !!p.User || (p.email && p.email.includes('@') && p.email.length > 0);
      return hasUserOrEmail;
    });
  }, [shouldLoadPlayers, playersRaw]);

  const handleContinue = () => {
    if (selectedSeasonIds.length === 0) {
      message.warning('Please select a season.');
      return;
    }
    setStep(STEPS.SELECT_PLAYERS);
  };

  const handleContinueToReview = () => {
    if (selectedPlayerIds.length === 0) {
      message.warning('Please select at least one player.');
      return;
    }
    setStep(STEPS.REVIEW_PLAYERS);
  };

  const handleSendInvites = () => {
    onSendInvites?.(selectedPlayers, selectedSeasonIds, () => setStep(STEPS.RESULTS));
  };

  const handleCancel = useCallback(() => {
    setStep(STEPS.SELECT_SEASON);
    setSelectedSeasonIds([]);
    setSelectedPlayerIds([]);
    
    onClose();
  }, [onClose]);

  // Get selected players data for review step
  const selectedPlayers = useMemo(() => {
    if (!players || selectedPlayerIds.length === 0) return [];
    return players.filter(p => selectedPlayerIds.includes(p.playerId));
  }, [players, selectedPlayerIds]);

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title="Invite Players to Season"
      footer={null}
      width={typeof window !== 'undefined' && window.innerWidth < 700 ? '95vw' : (window.innerWidth < 1400 ? '85vw' : 1400)}
      style={{
        top: '12vh',
        height: '85vh',
        minHeight: 300,
        display: 'flex',
        flexDirection: 'column',
      }}
      bodyStyle={{
        height: 'calc(100vh - 200px)', // 110px for header/footer
        display: 'flex',
        flexDirection: 'column',
        padding: typeof window !== 'undefined' && window.innerWidth < 700 ? 8 : 24,
        paddingBottom: 0,
        overflow: 'hidden',
      }}
    >
      {!isMobile && (
        <div className="md:px-2">
          <Steps size="small" current={step} className="mb-4">
            <Steps.Step title="Select Season" />
            <Steps.Step title="Select Players" />
            <Steps.Step title="Review Players" />
            <Steps.Step title="Results" />
          </Steps>
        </div>
      )}
      <div className="flex-1 flex flex-col min-h-0">
        {step === STEPS.SELECT_SEASON && (
          <>
            <PlayerInviteModalBody>
              <SeasonSelectionTable
                open={open}
                filters={{}}
                selectedRowKeys={selectedSeasonIds}
                onChange={setSelectedSeasonIds}
                scroll={{ y: 'calc(60vh - 56px)' }}
                header={
                  <div className="flex gap-2 items-center justify-start text-gray-600 ml-2">
                    <InfoCircleOutlined />
                    Only Seasons with 'Player Registration' set to Enabled and status 'Open for registration' or 'Running' are shown.
                  </div>
                }
              />
            </PlayerInviteModalBody>
            <PlayerInviteModalButtons>
              <Button size="small" onClick={handleCancel}>Cancel</Button>
              <Button size="small" type="primary" disabled={selectedSeasonIds.length === 0} onClick={handleContinue}>Continue</Button>
            </PlayerInviteModalButtons>
          </>
        )}
        {step === STEPS.SELECT_PLAYERS && (
          <>
            <PlayerInviteModalBody>
              <>
                {(playersLoading) ? (
                  <div className="flex-1 flex items-center justify-center"><Spin /></div>
                ) : (
                  <PlayerSelectionTable
                    filters={{}}
                    filterOutUsers={false}
                    showTeamsColumn={true}
                    selectedRowKeys={selectedPlayerIds}
                    onChange={setSelectedPlayerIds}
                    dataSource={players}
                    selectedSeasonIds={selectedSeasonIds}
                    header={
                      <div className="flex gap-2 items-center justify-start text-gray-600 ml-2">
                        <InfoCircleOutlined />
                        Only Players with an account or valid email address are shown.
                      </div>
                    }
                  />
                )}
              </>
            </PlayerInviteModalBody>
            <PlayerInviteModalButtons>
              <Button size="small" onClick={handleCancel}>Cancel</Button>
              <Button size="small" onClick={() => setStep(STEPS.SELECT_SEASON)}>Back</Button>
              <Button size="small" type="primary" disabled={selectedPlayerIds.length === 0} onClick={handleContinueToReview}>Continue</Button>
            </PlayerInviteModalButtons  >
          </>
        )}
        {step === STEPS.REVIEW_PLAYERS && (
          <>
            <PlayerInviteModalBody>
              <div className="mb-4">
                Please confirm inviting of the following {selectedPlayerIds.length} player(s):
              </div>
              <div className="flex-1 overflow-y-auto">
                {(playersLoading) ? (
                  <div className="flex-1 flex items-center justify-center"><Spin /></div>
                ) : (
                  <PlayerSelectionTable
                    readonly={true}
                    filters={{}}
                    filterOutUsers={false}
                    showTeamsColumn={true}
                    dataSource={selectedPlayers}
                    selectedSeasonIds={selectedSeasonIds}
                    header={null}
                  />
                )}
              </div>
            </PlayerInviteModalBody>
            <PlayerInviteModalButtons>
              <Button size="small" onClick={handleCancel}>Cancel</Button>
              <Button size="small" onClick={() => setStep(STEPS.SELECT_PLAYERS)}>Back</Button>
              <Button size="small" type="primary" loading={externalLoading} onClick={handleSendInvites}>
                Send {selectedPlayerIds.length} invite(s)
              </Button>
            </PlayerInviteModalButtons>
          </>
        )}
        {step === STEPS.RESULTS && inviteResults && (
          <>
            <PlayerInviteModalBody>
              <div className="mb-4">
                Operation finished:
              </div>
              <div className="flex-1 overflow-y-auto">
                <Table
                  rowKey={(r) => (r.playerId || '') + (r.email || '') + (r.seasonId || '')}
                  scroll={{ y: 'calc(60vh - 120px)' }}
                  columns={
                    isMobile ?
                      [
                        { title: 'Player',
                          render: (record) => {
                            return (
                              <div className="flex flex-col gap-2">
                                <div className="text-sm text-gray-600">Name: {record.firstName} {record.lastName}</div>
                                <div className="text-sm text-gray-600">E-mail: {record.email}</div>
                                <div className="text-sm text-gray-600">Season: {record.seasonName}</div>
                                <div className="text-sm text-gray-600">Status: {record.status === 'invited-new-user' || record.status === 'invited-existing-user' ? <span className="text-green-500">Invited</span> : record.status === 'error' ? <span className="text-red-500">Error</span> : record.status}</div>
                                <div className="text-sm text-gray-600">Message: {record.message}</div>
                              </div>
                            )
                          }
                        }
                      ]
                    :
                      [
                        { title: 'First name', dataIndex: 'firstName', key: 'firstName', width: 100 },
                        { title: 'Last name', dataIndex: 'lastName', key: 'lastName', width: 100 },
                        { title: 'Email', dataIndex: 'email', key: 'email', width: 220 },
                        { title: 'Season', dataIndex: 'seasonName', key: 'seasonName', width: 100 },
                        { title: 'Status', dataIndex: 'status', key: 'status', width: 100,
                          render: (status) => {
                            if (status === 'invited-new-user' || status === 'invited-existing-user') return <span className="text-green-500">Invited</span>;
                            if (status === 'error') return <span className="text-red-500">Error</span>;
                            return status;
                          }
                        },
                        { title: 'Message', dataIndex: 'message', key: 'message' },
                    ]
                  }
                  dataSource={inviteResults}
                  pagination={false}
                  size="small"
                  sticky={{ offsetHeader: 0 }}
                />
              </div>
            </PlayerInviteModalBody>
            <PlayerInviteModalButtons>
              <Button size="small" type="primary" onClick={handleCancel}>Close</Button>
            </PlayerInviteModalButtons>
          </>
        )}
      </div>
    </Modal>
  );
};

export default BulkInvitePlayerToSeasonDialog; 